import { useState } from "react"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import {
  breakpoint,
  Button,
  Skeleton,
  spacing,
} from "@minuthq/meatball-ui-react"

import { AiAssistantContextLibrary } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/AiAssistantContextLibrary"
import { AiAssistantCreateContextDialog } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantContextLibrary/AiAssistantCreateContextDialog"
import { AiAssistantPreview } from "src/components/AiAssistant/AiAssistantKnowledge/AiAssistantPreview/AiAssistantPreview"
import { EmptyStateWithImage } from "src/components/EmptyState/EmptyStateWithImage"
import NoPropertyContexts from "src/components/EmptyState/images/no-property-contexts.svg"
import { ScrollArea } from "src/components/ScrollArea/ScrollArea"
import { useBackendFlags } from "src/data/flags/useBackendFlags"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchPropertyContextCount } from "src/data/propertyContext/propertyContextQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { Container } from "src/ui/Container/Container"

export function AiAssistantKnowledge() {
  const { tPlaceholder } = useTranslate()

  const { compass_released } = useBackendFlags()
  const { org } = useOrganization()

  const [showCreateContextDialog, setShowCreateContextDialog] = useState(false)

  const fetchPropertyContextCount = useFetchPropertyContextCount({
    orgId: org.id,
  })

  if (!compass_released) {
    return <Redirect to={Routes.Dashboard.location()} />
  }

  if (fetchPropertyContextCount.isLoading) {
    return (
      <div>
        <Skeleton width="40ch" />
        <Skeleton width="30ch" />
        <Skeleton width="30ch" />
      </div>
    )
  }

  if (fetchPropertyContextCount.data === 0) {
    return (
      <AiAssistantKnowledgeLayout
        showCreateContextDialog={showCreateContextDialog}
        setShowCreateContextDialog={setShowCreateContextDialog}
      >
        <EmptyStateWithImage
          Image={NoPropertyContexts}
          imageSpacing={spacing.L}
          title={tPlaceholder("No knowledge added yet")}
          description={tPlaceholder(
            "Add content like house rules, check-in info, or policies. Autopilot will use it to answer questions from your guests accurately and consistently."
          )}
          content={
            <Button
              onClick={() => setShowCreateContextDialog(true)}
              size="small"
            >
              {tPlaceholder("Add content")}
            </Button>
          }
        />
      </AiAssistantKnowledgeLayout>
    )
  }

  return (
    <AiAssistantKnowledgeLayout
      showCreateContextDialog={showCreateContextDialog}
      setShowCreateContextDialog={setShowCreateContextDialog}
    >
      <Main>
        <StyledScrollArea>
          <AiAssistantContextLibrary
            setShowCreateContextDialog={setShowCreateContextDialog}
          />
        </StyledScrollArea>

        <AIPreviewScrollArea>
          <AiAssistantPreview />
        </AIPreviewScrollArea>
      </Main>
    </AiAssistantKnowledgeLayout>
  )
}

function AiAssistantKnowledgeLayout({
  children,
  showCreateContextDialog,
  setShowCreateContextDialog,
}: {
  children: React.ReactNode
  showCreateContextDialog: boolean
  setShowCreateContextDialog: (show: boolean) => void
}) {
  return (
    <StyledContainer>
      {children}

      {showCreateContextDialog && (
        <AiAssistantCreateContextDialog
          onClose={() => setShowCreateContextDialog(false)}
        />
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Main = styled.div`
  flex: 1;

  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 2fr;
  gap: ${spacing.L};
  overflow: hidden;

  @container (${breakpoint.mediumUp}) {
    grid-template-columns: 1.5fr 1fr;
    grid-template-rows: 1fr;
  }

  @container (${breakpoint.normalUp}) {
    grid-template-columns: 2fr 1fr;
  }

  @container (${breakpoint.largeUp}) {
    grid-template-columns: 3fr 1fr;
  }
`

const StyledScrollArea = styled(ScrollArea)`
  padding-right: ${spacing.S};
`

const AIPreviewScrollArea = styled(ScrollArea)`
  @container (${breakpoint.mediumUp}) {
    height: auto;
  }
`
